import { Level, RedFlags, ScoreGuideItem, Section } from './tech-due-diligence-quiz.types';

export const LEVEL_COLORS: Record<Level, string> = {
  L4: '#E2ED8D',
  L3: '#03D2FC',
  L2: '#FFCA6C',
  L1: '#FE9B74',
};

export const LEVEL_TEXT_COLORS: Record<Level, string> = {
  L4: '#727F0A',
  L3: '#0A6C7F',
  L2: '#7F550A',
  L1: '#7F2B0A',
};

export const SECTIONS: Section[] = [
  {
    id: 's1',
    title: 'Delivery Process & Engineering Maturity',
    weight: 0.25,
    questions: [
      {
        topic: 'Task planning & delivery predictability',
        levels: {
          L4: 'Structured planning with measurable predictability (velocity, throughput, cycle time). Team reliably hits committed scope.',
          L3: 'Planning tool in use, reasonable cadence, but predictability not measured formally.',
          L2: 'Tasks tracked but planning slips — commitments shift mid-cycle.',
          L1: 'Ad hoc — tasks assigned informally, no structured planning.',
        },
        why: "Teams that don't track velocity can't predict a deadline. Every release date is a guess.",
      },
      {
        topic: 'CI/CD & release process',
        levels: {
          L4: 'Fully automated pipeline: commit → test → build → deploy. Rollback in place. Median deploy under 30 minutes end-to-end.',
          L3: 'CI automated, CD partially manual — deploys require a human step.',
          L2: 'Some automation (tests or build), but deployment is manual.',
          L1: "Fully manual — scripts, direct access, or 'we deploy when ready'.",
        },
        why: 'Manual deploys are the #1 predictor of release risk. No CI/CD = no predictable delivery.',
      },
      {
        topic: 'Monitoring & incident response',
        levels: {
          L4: 'Alerts, logs, tracing across critical paths. Formal incident management process. Team detects issues before customers report them.',
          L3: 'Basic monitoring in place, some alerts — but coverage is incomplete. Team usually detects issues in parallel with customers.',
          L2: 'Logs exist, but incidents are usually reported by customers first.',
          L1: 'No systematic monitoring — team reacts when users complain.',
        },
        why: "Every hour of undetected downtime costs SaaS companies ~$300K. If customers tell you first — there's no monitoring.",
      },
      {
        topic: 'Testing discipline',
        levels: {
          L4: 'Multi-layer testing (unit + integration + e2e), ≥70% coverage on critical paths. Tests block merge in CI as standard practice.',
          L3: 'Solid unit + integration coverage, e2e partial. Coverage tracked but not blocking. Tests written for most new features.',
          L2: 'Only unit tests, uneven coverage. Tests exist but often skipped or broken. No enforced policy.',
          L1: 'Minimal or no automated tests. QA is manual or absent. Regressions ship regularly.',
        },
        why: 'CI/CD without tests just ships bugs faster. Test coverage on critical paths is the strongest predictor of release failures.',
      },
      {
        topic: 'Incident learning loop',
        levels: {
          L4: 'Blameless post-mortems after every SEV1/SEV2, with tracked action items (owners, due dates) and recurring causes eliminated.',
          L3: 'Post-mortems for major incidents, action items created but tracked informally. Some recurrence.',
          L2: 'No post-mortem culture — same class of incident repeats.',
          L1: 'No incident review happens at all — failures are forgotten as soon as service is restored.',
        },
        why: 'Detecting incidents is half the game — without a learning loop, teams repeat the same outage. Post-mortems are a maturity signal.',
      },
    ],
  },
  {
    id: 's2',
    title: 'Codebase Health & Security',
    weight: 0.25,
    questions: [
      {
        topic: 'Technology stack currency',
        levels: {
          L4: 'Stack is current — actively maintained versions, regular dependency updates, no EOL components in production.',
          L3: 'Core stack is reasonably modern, but some libraries are outdated — updates happen reactively.',
          L2: "Several key dependencies are significantly out of date — team hasn't prioritised updates.",
          L1: 'Stack includes EOL languages or libraries with known vulnerabilities — no update process.',
        },
        why: 'Outdated dependencies are the most common source of exploitable vulnerabilities and make hiring harder.',
      },
      {
        topic: 'Static analysis & vulnerability scanning',
        levels: {
          L4: 'SAST and dependency scanning integrated into CI/CD — vulnerabilities block deployment. Reports reviewed regularly.',
          L3: 'Scanning tools in use but not blocking — results reviewed periodically, not every commit.',
          L2: 'Tools exist but rarely used — reports generated but not actioned.',
          L1: 'No automated scanning — security review is manual or absent.',
        },
        why: 'Unscanned codebases carry silent vulnerabilities — a single critical CVE can trigger regulatory action or churn.',
      },
      {
        topic: 'Human access & identity management',
        levels: {
          L4: 'Centralised IdM + RBAC + SSO/MFA enforced everywhere. Access reviewed quarterly. Leavers offboarded within 24h.',
          L3: 'Centralised IdM in place, RBAC exists but reviewed only annually. Offboarding mostly reliable.',
          L2: 'Access managed manually per-tool. Developers often have broader access than their role requires.',
          L1: 'Shared credentials with no role separation or reliable revocation.',
        },
        why: 'Overprivileged and stale access is a leading cause of insider incidents and compliance failures (GDPR/SOC2).',
      },
      {
        topic: 'Secrets & machine credentials',
        levels: {
          L4: 'Managed secrets store (Vault, AWS/GCP/Azure). Secrets never in code. Automated rotation, audited access.',
          L3: 'Secrets store in production; rotation is manual and irregular.',
          L2: 'Secrets in CI environment variables or .env files. No rotation policy.',
          L1: 'Secrets in Slack, email, or git history. No inventory of who has what.',
        },
        why: 'Leaked prod credentials are the fastest path from a small breach to a company-ending incident.',
      },
      {
        topic: 'Backups, DR & data resilience',
        levels: {
          L4: 'Automated backups with documented RPO/RTO. Restore tested quarterly. Multi-region backup copy exists.',
          L3: 'Automated backups in place, tested once in the last 12 months.',
          L2: 'Backups exist but restore is never tested. RPO/RTO undefined.',
          L1: "No backups, or 'our cloud provider handles it' with no verification.",
        },
        why: 'Untested backups fail on the day you need them — teams are one incident away from catastrophic loss.',
      },
      {
        topic: 'Compliance & data protection posture',
        levels: {
          L4: 'Applicable frameworks identified (GDPR, SOC2, etc). Active certification. DPAs with all sub-processors.',
          L3: 'Frameworks identified, compliance work in progress.',
          L2: 'Aware of what applies but no active program; DPAs incomplete.',
          L1: 'No compliance program or awareness of applicable frameworks.',
        },
        why: 'Compliance status determines which customers you can sell to — missing DPAs cost a full deal cycle to fix.',
      },
    ],
  },
  {
    id: 's3',
    title: 'AI Tool Adoption & Team Capability',
    weight: 0.15,
    questions: [
      {
        topic: 'AI tooling in the engineering workflow',
        levels: {
          L4: 'Multiple tools team-wide: coding assistant, AI code review, test and doc generation. Structured rollout.',
          L3: '1–2 tools in use (e.g. Copilot) but adoption is uneven across the team.',
          L2: 'A few engineers use AI tools individually — no team-wide adoption.',
          L1: 'No AI tools in use in the engineering workflow.',
        },
        why: "Systematic AI adoption ships 25–40% faster (GitHub data) — not just 'one engineer has Copilot'.",
      },
      {
        topic: 'Measuring AI impact',
        levels: {
          L4: 'Velocity, PR cycle time, or bug rate tracked before/after AI adoption — real data.',
          L3: 'Some engineering metrics tracked, but not linked to AI tooling.',
          L2: "'Feels faster' — no measurement.",
          L1: 'No team performance metrics tracked at all.',
        },
        why: "'Seems faster' isn't an argument for the board — without data you can't prove ROI.",
      },
      {
        topic: 'Team learning & skill currency',
        levels: {
          L4: 'Regular internal knowledge sharing, structured learning budget, documented adoption of new tools.',
          L3: 'Informal learning — engineers follow the field, no formal process.',
          L2: 'Learning happens reactively — when a problem forces it.',
          L1: 'No visible investment in team development.',
        },
        why: 'A team that stops learning falls behind in 6–12 months — a talent retention signal.',
      },
    ],
  },
  {
    id: 's4',
    title: 'Architecture & Scalability',
    weight: 0.2,
    questions: [
      {
        topic: 'Architecture documentation & clarity',
        levels: {
          L4: 'Clear architecture diagram, documented decisions (ADRs) — any senior engineer can explain it.',
          L3: 'Architecture understood by the team but not formally documented.',
          L2: 'Partially documented — some components clear, others not.',
          L1: 'No documentation — only the original author understands the system.',
        },
        why: "If only one person can explain the architecture, it's key-person risk wrapped in a technical problem.",
      },
      {
        topic: 'Architecture model & infrastructure maturity',
        topicShort: 'Architecture & Infrastructure Maturity',
        levels: {
          L4: 'Microservices or modular monolith with clear boundaries. Kubernetes in production, IaC, auto-scaling.',
          L3: 'Containerised (Docker) with partial orchestration, moving toward microservices. Some IaC in place.',
          L2: 'Monolith with no decomposition plan. Containers used in dev only.',
          L1: 'Legacy monolith, no containerisation. Infrastructure managed manually. No scaling strategy.',
        },
        why: "A monolith isn't automatically a problem — but no path to scalability is. Manual infra = single point of failure.",
      },
      {
        topic: 'Tech roadmap alignment',
        levels: {
          L4: 'Tech roadmap exists, reviewed quarterly, tied to business milestones and product goals.',
          L3: 'Tech priorities exist informally, not yet documented.',
          L2: 'Tech work is reactive — engineering follows product without a plan.',
          L1: 'No tech roadmap — engineering is entirely feature-driven with no strategic view.',
        },
        why: 'Misalignment between tech and business roadmap is the #1 cause of post-investment delivery failure.',
      },
      {
        topic: 'Scalability evidence & SLOs',
        levels: {
          L4: 'Formal SLOs with error budgets. Load-tested at 5–10× current traffic within 6 months, bottlenecks documented.',
          L3: 'Some reliability targets defined informally. Basic load testing done, but not recently or systematically.',
          L2: "Rough headroom estimates ('we should handle 2× current'). No load testing. No formal SLOs.",
          L1: "No idea what breaks first at scale. No load testing ever — 'we'll deal with it when we get there.'",
        },
        why: "Post-investment growth usually means 3–10× traffic — teams that haven't tested this find out the hard way.",
      },
    ],
  },
  {
    id: 's5',
    title: 'Key-Person Risk & Knowledge Management',
    weight: 0.15,
    questions: [
      {
        topic: 'Key-person dependency',
        levels: {
          L4: 'Knowledge distributed across the team. Any critical area has ≥2 people who can own it. Documented ownership map.',
          L3: 'Some dependency on key people, acknowledged and actively addressed through pairing and docs.',
          L2: '1–2 people hold critical knowledge — their departure would cause significant disruption.',
          L1: 'Only the CTO or founder understands the whole system.',
        },
        why: 'Key-person dependency is an investment risk, not just a management problem — it affects valuation.',
      },
      {
        topic: 'Onboarding & team scalability',
        levels: {
          L4: '<2 weeks to productivity, structured onboarding guide, all environments and access documented.',
          L3: '2–4 weeks, mostly informal — new engineers figure it out with help from the team.',
          L2: '4–8 weeks, painful — new engineers frequently blocked on undocumented dependencies.',
          L1: "No onboarding process — 'just ask whoever built it'.",
        },
        why: 'If it takes 8 weeks to onboard one engineer, scaling the team post-investment takes 12 months, not 3.',
      },
      {
        topic: 'Role definitions & personal development',
        levels: {
          L4: 'Role descriptions documented with clear scope per level. Each engineer has an active PDP reviewed quarterly.',
          L3: "Role definitions exist informally — CTO can articulate them, but they're not documented.",
          L2: 'Roles defined by title only — no documented expectations or development conversations.',
          L1: 'No role definitions and no development process — engineers self-direct or stagnate.',
        },
        why: 'Undefined roles and absent development are the leading cause of senior attrition at 18–24 months.',
      },
    ],
  },
];

export const LEVEL_LEGEND: { level: Level; label: string }[] = [
  { level: 'L4', label: 'Best in class' },
  { level: 'L3', label: 'Operational' },
  { level: 'L2', label: 'Emerging' },
  { level: 'L1', label: 'Foundation risk' },
];

export const RED_FLAGS: RedFlags = {
  categories: [
    {
      id: 'instantKill',
      title: 'Instant Kill',
      flagLabel: 'Red flags',
      flagIconColor: '#7F2B0A',
      flagBgColor: '#FFEBE3',
      items: [
        'Production secrets or credentials found in public git history',
        'Undisclosed prior data breach or security incident in the last 24 months',
        'Active legal proceedings related to IP, employment, or data protection',
        'No IP assignment agreements from key engineers or contractors',
        'Critical unpatched CVE (CVSS ≥9.0) in production, publicly exploitable',
        'No access control policy — shared credentials in production use',
        'Missing valid legal entity, tax registration, or operating licenses in contract jurisdiction',
      ],
    },
    {
      id: 'majorMitigation',
      title: 'Major Mitigation',
      flagLabel: 'Yellow flags',
      flagIconColor: '#7F550A',
      flagBgColor: '#FFF4E2',
      items: [
        'CTO is also the only senior engineer',
        "Engineers can't explain the architecture without the founder",
        'No version control history older than 12 months — recent rewrite',
        'Fixed-price agency contract still active for core product development',
        'Stack includes EOL framework, language, or database',
        'Unclosed critical or high findings from last pen-test',
        'Missing DPAs with critical sub-processors (cloud, LLM, payments)',
        'Missing required market compliance cert (SOC2, ISO27001, PCI, HIPAA)',
        'Critical path depends on a single third-party API with no fallback',
        '>6 months since last successful production deploy of the core service',
      ],
    },
    {
      id: 'watchItem',
      title: 'Watch Item',
      flagLabel: 'Grey flags',
      flagIconColor: '#1d1d1f',
      flagBgColor: '#F1F2F2',
      items: [
        "'The team knows how it works' — no docs",
        "'We'll fix the tech debt after we close'",
        'No staging environment — everything tested in production',
        "'We've never had downtime'",
        '>25% voluntary engineering attrition in last 6 months',
        'Founder/CTO equity fully vested, no continuity commitment',
        'No documented data retention or deletion policy for customer data',
        'Repeated hiring failures — >2 senior engineers left within probation in last 12 months',
      ],
    },
  ],
};

export const SCORE_GUIDE: ScoreGuideItem[] = [
  { level: 'L4', range: '3.5–4.0', min: 3.5, label: 'High-Performance', desc: 'Ready to scale.' },
  { level: 'L3', range: '2.5–3.4', min: 2.5, label: 'Operational', desc: 'Solid, but needs a measurement layer.' },
  { level: 'L2', range: '1.8–2.4', min: 1.8, label: 'Emerging', desc: 'Post-investment support required.' },
  { level: 'L1', range: '1.0–1.7', min: 1.0, label: 'Foundation', desc: 'Adjust valuation or negotiate terms.' },
];

export function scoreGuideFor(score: number): ScoreGuideItem {
  return SCORE_GUIDE.find((guide) => score >= guide.min) ?? SCORE_GUIDE[SCORE_GUIDE.length - 1];
}
